import React, { useState } from 'react';
import { X, Banknote } from 'lucide-react';
import { supabase } from '../lib/supabase';
import type { RegistryItem } from '../types/registry';

const MONEY_PRESETS = [5000, 10000, 15000, 20000];

interface Props {
  item: Partial<RegistryItem> & {
    name: string;
    price: number;
    priority: 'high' | 'medium' | 'low';
    allows_group_gifting: boolean;
  };
  currency: string;
  onClose: () => void;
  onUpdate: (item: Omit<RegistryItem, 'id' | 'registry_id' | 'is_claimed' | 'claimed_by' | 'created_at' | 'collected_amount'>) => void;
}

export default function EditItemModal({ item, currency, onClose, onUpdate }: Props) {
  const [name, setName] = useState(item.name);
  const [price, setPrice] = useState(item.price.toString());
  const [url, setUrl] = useState(item.url || '');
  const [priority, setPriority] = useState(item.priority);
  const [allowsGroupGifting, setAllowsGroupGifting] = useState(item.allows_group_gifting);
  const [targetAmount, setTargetAmount] = useState(item.target_amount?.toString() || '');
  const [isMoneyGift, setIsMoneyGift] = useState(item.is_money_gift || false);
  const [moneyAmount, setMoneyAmount] = useState(item.money_amount?.toString() || '');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError('');

      const updateData = {
        name,
        price: isMoneyGift ? Number(moneyAmount) : Number(price),
        url: isMoneyGift ? null : (url || null),
        priority,
        allows_group_gifting: isMoneyGift ? false : allowsGroupGifting,
        target_amount: isMoneyGift ? null : (allowsGroupGifting ? Number(targetAmount) : null),
        is_money_gift: isMoneyGift,
        money_amount: isMoneyGift ? Number(moneyAmount) : null
      };

      if (!item.id) {
        // This is a new item, let the parent handle the creation
        onUpdate(updateData);
      } else {
        // This is an existing item, update it directly
        const { error: updateError } = await supabase
          .from('registry_items')
          .update(updateData)
          .eq('id', item.id);

        if (updateError) throw updateError;
        onUpdate(updateData);
      }

      onClose();
    } catch (err) {
      console.error('Error updating item:', err);
      setError('Failed to update item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/20 flex items-center justify-center p-4 z-50">
      <div className="card-pastel p-6 max-w-md w-full">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-800 font-poppins">
            {item.id ? 'Edit Gift Item' : 'Add Gift Item'}
          </h3>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/60 rounded-full transition-colors text-gray-500 hover:text-gray-800"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {error && (
          <div className="mb-4 bg-red-50 text-red-700 p-3 rounded-md">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Money Gift Toggle */}
          <div className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id="isMoneyGift"
                checked={isMoneyGift}
                onChange={(e) => {
                  setIsMoneyGift(e.target.checked);
                  if (e.target.checked) {
                    setAllowsGroupGifting(false);
                  }
                }}
                className="h-4 w-4 bg-white/60 border-white/40 rounded text-primary focus:ring-primary"
              />
              <label htmlFor="isMoneyGift" className="ml-2 text-sm text-gray-600 flex items-center gap-1.5 font-poppins">
                <Banknote className="w-4 h-4 text-green-400" />
                Money Gift (multiple guests can claim)
              </label>
            </div>

            {isMoneyGift && (
              <div className="space-y-2 pl-6 border-l-2 border-green-200">
                <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                  Preset Amount ({currency})
                </label>
                <div className="grid grid-cols-4 gap-2">
                  {MONEY_PRESETS.map(amt => (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => {
                        setMoneyAmount(amt.toString());
                        if (!name || name.startsWith('Money Gift')) {
                          setName(`Money Gift - ${currency} ${amt.toLocaleString()}`);
                        }
                      }}
                      className={`px-2 py-2 text-xs rounded-md font-medium transition-colors font-poppins ${
                        moneyAmount === amt.toString()
                          ? 'bg-green-600 text-white'
                          : 'bg-white/60 border border-white/40 text-gray-600 hover:bg-white/80'
                      }`}
                    >
                      {currency} {amt.toLocaleString()}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  value={moneyAmount}
                  onChange={(e) => setMoneyAmount(e.target.value)}
                  className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                  placeholder="Or enter custom amount"
                  min="0"
                  step="0.01"
                  required
                />
              </div>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
              Item Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
              required
            />
          </div>

          {!isMoneyGift && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                  Price ({currency})
                </label>
                <input
                  type="number"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                  className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                  required
                  min="0"
                  step="0.01"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                  Product URL
                </label>
                <input
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                  placeholder="https://"
                />
              </div>
            </>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
              Priority
            </label>
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value as 'high' | 'medium' | 'low')}
              className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
            >
              <option value="high">High</option>
              <option value="medium">Medium</option>
              <option value="low">Low</option>
            </select>
          </div>

          {!isMoneyGift && (
            <div className="space-y-2">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="allowsGroupGifting"
                  checked={allowsGroupGifting}
                  onChange={(e) => setAllowsGroupGifting(e.target.checked)}
                  className="h-4 w-4 bg-white/60 border-white/40 rounded text-primary focus:ring-primary"
                />
                <label htmlFor="allowsGroupGifting" className="ml-2 text-sm text-gray-600 font-poppins">
                  Allow Group Gifting
                </label>
              </div>

              {allowsGroupGifting && (
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1 font-poppins">
                    Target Amount ({currency})
                  </label>
                  <input
                    type="number"
                    value={targetAmount}
                    onChange={(e) => setTargetAmount(e.target.value)}
                    className="w-full px-3 py-2 bg-white/60 border border-white/40 text-gray-800 rounded-md focus:ring-primary focus:border-primary font-poppins"
                    required
                    min={price}
                    step="0.01"
                  />
                </div>
              )}
            </div>
          )}

          <div className="flex justify-end space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-gray-600 hover:text-gray-800 font-poppins"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-600 disabled:opacity-50 font-poppins"
            >
              {loading ? 'Saving...' : item.id ? 'Save Changes' : 'Add Item'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}